import { PagePicker } from './PagePicker.tsx';
import { usePersistentState } from '../../core/usePersistentState.ts';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { quoteBudget, budgetOffset } from '../../../../shared/budget.ts';
import { api } from '../../core/apiClient.ts';
import type { AdAccount, Page, Payer, Pixel, WizardTarget } from './useLaunchWizard.ts';

type Props = {
  accounts: AdAccount[];
  pages: Page[];
  targets: Record<string, WizardTarget[]>;
  pixels: Record<string, Pixel[]>;
  payers: Record<string, Payer[]>;
  allPayers: Payer[];
  selectedOrder: string[];
  onSelect: (id: string, yes: boolean) => void;
  onUpdate: (accountId: string, localId: string, patch: Partial<WizardTarget>) => void;
  onBulkUpdateAll: (patch: Partial<WizardTarget>) => void;
  onAddCampaign: (accountId: string) => void;
  onSetCampaignCount: (count: number) => void;
  onRemoveRow: (accountId: string, localId: string) => void;
  onBulkSelect: (ids: string[]) => void;
  loadPixels: (id: string) => void;
  loadPayers: (id: string) => void;
  templateBudget: number | null;
};

function BulkAccsModal({ accounts, onAdd, onClose }: { accounts: AdAccount[]; onAdd: (ids: string[]) => void; onClose: () => void }) {
  const [text, setText] = useState('');

  const handleAdd = () => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const found: string[] = [];
    for (const line of lines) {
      // strip act_ prefix if present
      const clean = line.replace(/^act_/, '');
      const match = accounts.find((a) => a.id === clean || a.id === line);
      if (match) found.push(match.id);
    }
    if (found.length) onAdd(found);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk add accounts</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>
        <p className="text-muted text-sm">Paste ad account IDs — one per line. They will be auto-selected.</p>
        <textarea
          className="input modal-textarea"
          placeholder={'123456789\n987654321\n...'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <div className="modal-actions">
          <button className="btn btn--secondary btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleAdd}>Add</button>
        </div>
      </div>
    </div>
  );
}

export function CabinetsStep({ accounts, pages, targets, pixels, payers, allPayers, selectedOrder, templateBudget, onSelect, onUpdate, onBulkUpdateAll, onAddCampaign, onSetCampaignCount, onRemoveRow, onBulkSelect, loadPixels, loadPayers }: Props) {
  const [rates, setRates] = useState<Record<string, number>>({ USD: 1 });
  const [ratesError, setRatesError] = useState(false);
  useEffect(() => {
    api.get<Array<{ code: string; usd_rate: number | null }>>('/api/currency-rates')
      .then((rows) => { setRates(Object.fromEntries(rows.filter((row) => row.usd_rate != null).map((row) => [row.code, Number(row.usd_rate)]))); setRatesError(false); })
      .catch(() => setRatesError(true));
  }, []);

  const budgetHint = (currencyValue: string | null, amount: number | null) => {
    const currency = currencyValue?.trim().toUpperCase();
    if (!currency) return 'Валюта кабінету невідома';
    const rate = currency === 'USD' ? 1 : rates[currency];
    if (!Number.isFinite(rate) || rate <= 0) return ratesError
      ? 'Не вдалося завантажити курси валют'
      : <>Для {currency} не задано курс. Введіть його у вкладці <Link to={`/currency-rates?code=${encodeURIComponent(currency)}`}>Курси валют</Link>.</>;
    if (amount == null || !Number.isFinite(amount) || amount <= 0) return 'Введіть бюджет у USD';
    try {
      const quote = quoteBudget(amount, rate, budgetOffset(currency));
      return `≈ ${quote.nativeAmount.toLocaleString('uk-UA', { maximumFractionDigits: 2 })} ${currency}`;
    } catch { return 'Недопустимий бюджет у USD'; }
  };
  const [search, setSearch] = usePersistentState('launch.search', '');
  const [onlySelected, setOnlySelected] = useState(() => sessionStorage.getItem('launch-only-selected') === 'true');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [campaignCount, setCampaignCount] = useState(5);
  const [copiedPixelId, setCopiedPixelId] = useState<string | null>(null);

  const copyPixel = async (pixelId: string | null) => {
    if (!pixelId) return;
    await navigator.clipboard.writeText(pixelId);
    setCopiedPixelId(pixelId);
    window.setTimeout(() => setCopiedPixelId(null), 1800);
  };

  const setOnlySelectedWithStorage = (val: boolean) => {
    setOnlySelected(val);
    sessionStorage.setItem('launch-only-selected', String(val));
  };

  // Build a map for O(1) lookup
  const accountMap = Object.fromEntries(accounts.map((a) => [a.id, a]));

  // Determine which accounts to show, in the correct order.
  // Selected accounts appear first — in the order they were selected.
  // Unselected accounts follow, in backend order (alphabetical).
  let visible: AdAccount[];
  if (onlySelected) {
    // Only show selected, in selection order
    visible = selectedOrder.map((id) => accountMap[id]).filter(Boolean);
  } else {
    // Show selected first (in selection order), then remaining unselected accounts
    const selectedSet = new Set(selectedOrder);
    const unselected = accounts.filter((a) => !selectedSet.has(a.id));
    const orderedSelected = selectedOrder.map((id) => accountMap[id]).filter(Boolean);
    visible = [...orderedSelected, ...unselected];
  }

  // Apply search filter
  if (search) {
    const q = search.toLowerCase();
    visible = visible.filter((a) => `${a.id} ${a.name ?? ''}`.toLowerCase().includes(q));
  }

  const handleBulkAdd = (ids: string[]) => {
    onBulkSelect(ids);
    setOnlySelectedWithStorage(true);
  };

  const hasSelectedRows = selectedOrder.length > 0;

  // Get pages filtered for a given ad account's social_account_id
  const getPagesForAccount = (account: AdAccount): Page[] => {
    if (!account.social_account_id) return pages; // fallback: show all if no social_account_id
    return pages.filter((p) => p.social_account_id === account.social_account_id || !p.social_account_id);
  };

  return (
    <div className="wizard-step">
      {showBulkModal && (
        <BulkAccsModal
          accounts={accounts}
          onAdd={handleBulkAdd}
          onClose={() => setShowBulkModal(false)}
        />
      )}

      <div className="inline-fields">
        <input className="input" placeholder="Search ID or account name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="toggle">
          <input
            type="checkbox"
            checked={onlySelected}
            onChange={(e) => setOnlySelectedWithStorage(e.target.checked)}
          /> Only selected
        </label>
        <button className="btn btn--outline btn--sm" onClick={() => setShowBulkModal(true)}>⚡ Bulk Accs</button>
      </div>

      {selectedOrder.length > 0 && <div className="inline-fields" style={{ marginTop: 'var(--space-2)' }}>
        <label className="field"><span className="label">Campaigns per account</span><input className="input" type="number" min="1" value={campaignCount} onChange={(event) => setCampaignCount(Math.max(1, Number(event.target.value)))} /></label>
        <button className="btn btn--outline btn--sm" onClick={() => onSetCampaignCount(campaignCount)}>Apply count</button>
      </div>}

      <div className="wizard-table">
        <div className="wizard-row wizard-row--header">
          <span></span>
          <span>Ad account</span>
          <span>Fan Page</span>
          <span>Pixel</span>
          <span>Link</span>
          <span>Budget (USD)</span>
          <span>Additional</span>
          <span></span>
        </div>

        {/* Apply-to-all row — visible only when at least one account is selected */}
        {hasSelectedRows && (
          <div className="wizard-row wizard-row--apply-all">
            <span></span>
            <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>Apply to all</div>

            {/* Fan Page — apply to all rows (also clears post_id) */}
            <PagePicker pages={pages} value="" placeholder="Page for all" onChange={(id) => { if (id) onBulkUpdateAll({ page_id: id, post_id: '' }); }} />

            {/* Pixel — apply to all rows */}
            <select
              className="input"
              value=""
              onChange={(e) => {
                if (e.target.value) onBulkUpdateAll({ pixel_id: e.target.value });
              }}
            >
              <option value="">— Pixel for all —</option>
              {/* Collect unique pixels across all selected accounts */}
              {Array.from(
                new Map(
                  selectedOrder.flatMap((id) => (pixels[id] ?? []).map((px) => [px.id, px]))
                ).values()
              ).map((px) => <option key={px.id} value={px.id}>{`${px.name ?? px.id} (${px.id})`}</option>)}
            </select>

            {/* Link */}
            <input
              className="input"
              placeholder="Link for all"
              onBlur={(e) => { if (e.target.value) { onBulkUpdateAll({ link_url: e.target.value }); e.target.value = ''; } }}
            />

            {/* Budget */}
            <input
              className="input"
              type="number"
              placeholder="Budget USD for all"
              onBlur={(e) => { if (e.target.value) { onBulkUpdateAll({ budget_override: Number(e.target.value) }); e.target.value = ''; } }}
            />

            {/* Additional: Saved beneficiary + beneficiary input + URL tags */}
            <div className="payer-fields">
              {/* Saved beneficiary — aggregated from all loaded payers */}
              <select
                className="input"
                value=""
                onChange={(e) => {
                  const payer = allPayers[Number(e.target.value)];
                  if (payer) {
                    const name = payer.beneficiaryName ?? payer.payerName ?? '';
                    onBulkUpdateAll({ beneficiary_name: name, payer_name: name });
                  }
                }}
              >
                <option value="">Saved beneficiary (all)</option>
                {allPayers.map((payer, i) => (
                  <option key={i} value={i}>
                    {payer.beneficiaryName ?? payer.payerName}
                  </option>
                ))}
              </select>
              <input
                className="input"
                placeholder="Beneficiary for all"
                onBlur={(e) => { if (e.target.value) { onBulkUpdateAll({ beneficiary_name: e.target.value, payer_name: e.target.value }); e.target.value = ''; } }}
              />
              <textarea
                className="input"
                placeholder="URL tags for all"
                onBlur={(e) => { if (e.target.value) { onBulkUpdateAll({ url_tags: e.target.value }); e.target.value = ''; } }}
              />
            </div>

            <span></span>
          </div>
        )}

        {visible.map((account) => {
          const rows = targets[account.id] ?? [];
          const accountPages = getPagesForAccount(account);
          return (
            <React.Fragment key={account.id}>
              {rows.length === 0 && (
                // Unselected account — checkbox + name only, no fields
                <div className="wizard-row">
                  <label>
                    <input type="checkbox" checked={false} onChange={(e) => onSelect(account.id, e.target.checked)} />
                  </label>
                  <div>
                    <strong>{account.name ?? account.id}</strong>
                    <small>{account.id} · {account.currency ?? '—'}</small>
                  </div>
                </div>
              )}

              {rows.map((target, idx) => (
                <div className="wizard-row" key={target.local_id}>
                  <label>
                    <input
                      type="checkbox"
                      checked
                      onChange={(e) => {
                        if (e.target.checked) return;
                        // If last row — deselect account; otherwise remove just this row
                        if (rows.length === 1) onSelect(account.id, false);
                        else onRemoveRow(account.id, target.local_id);
                      }}
                    />
                  </label>
                  <div>
                    {idx === 0 ? (
                      <>
                        <strong>{account.name ?? account.id}</strong>
                        <small>{account.id} · {account.currency ?? '—'}</small>
                      </>
                    ) : (
                      <small className="text-muted">↳ Campaign {idx + 1}</small>
                    )}
                  </div>

                  <PagePicker pages={accountPages} value={target.page_id} onChange={(id) => onUpdate(account.id, target.local_id, { page_id: id, post_id: '' })} />

                  <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
                    <select
                      className={`input ${!target.pixel_id ? 'input--invalid' : ''}`}
                      style={{ minWidth: 0, flex: 1 }}
                      value={target.pixel_id ?? ''}
                      onFocus={() => loadPixels(account.id)}
                      onChange={(e) => onUpdate(account.id, target.local_id, { pixel_id: e.target.value || null })}
                    >
                      <option value="">
                        {pixels[account.id]
                          ? (pixels[account.id].length > 0 ? 'Select Pixel (required)' : 'No pixels found')
                          : 'Loading Pixels…'}
                      </option>
                      {(pixels[account.id] ?? []).map((pixel) => (
                        <option value={pixel.id} key={pixel.id}>{`${pixel.name ?? pixel.id} (${pixel.id})`}</option>
                      ))}
                    </select>
                    <button className="btn btn--secondary btn--sm" disabled={!target.pixel_id} onClick={() => copyPixel(target.pixel_id)} title="Copy Pixel ID">
                      {target.pixel_id && copiedPixelId === target.pixel_id ? '✓' : '⧉'}
                    </button>
                  </div>

                  <input className="input" placeholder="Link (optional)" value={target.link_url} onChange={(e) => onUpdate(account.id, target.local_id, { link_url: e.target.value })} />

                  <div>
                    <input className="input" type="number" min="0.01" step="0.01" placeholder={templateBudget ? `USD (${templateBudget})` : 'Budget USD'} value={target.budget_override ?? ''} onChange={(e) => onUpdate(account.id, target.local_id, { budget_override: e.target.value ? Number(e.target.value) : null })} />
                    <small>{budgetHint(account.currency, target.budget_override ?? templateBudget)}</small>
                  </div>

                  <div className="payer-fields">
                    <select
                      className="input"
                      onFocus={() => loadPayers(account.id)}
                      onChange={(e) => {
                        const payer = (payers[account.id] ?? [])[Number(e.target.value)];
                        if (payer) onUpdate(account.id, target.local_id, { beneficiary_name: payer.beneficiaryName ?? payer.payerName ?? '', payer_name: payer.beneficiaryName ?? payer.payerName ?? '' });
                      }}
                    >
                      <option>Saved beneficiary</option>
                      {(payers[account.id] ?? []).map((payer, i) => <option key={i} value={i}>{payer.beneficiaryName ?? payer.payerName}</option>)}
                    </select>
                    <input className="input" placeholder="Beneficiary / payer" value={target.beneficiary_name} onChange={(e) => onUpdate(account.id, target.local_id, { beneficiary_name: e.target.value, payer_name: e.target.value })} />
                    <textarea className="input" placeholder="URL tags" value={target.url_tags} onChange={(e) => onUpdate(account.id, target.local_id, { url_tags: e.target.value })} />
                  </div>

                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      if (rows.length === 1) onSelect(account.id, false);
                      else onRemoveRow(account.id, target.local_id);
                    }}
                    title="Remove this campaign"
                  >✕</button>
                </div>
              ))}

              {rows.length > 0 && (
                <div className="wizard-row" style={{ gridColumn: '1 / -1' }}>
                  <button
                    className="btn btn--outline btn--sm"
                    onClick={() => onAddCampaign(account.id)}
                  >
                    + Add another campaign for this account
                  </button>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
